import {
  NOTIFICATION_AUDIENCES,
  NOTIFICATION_CHANNELS,
  NOTIFICATION_EVENT_NAMES,
  NOTIFICATION_FREQUENCIES,
  NOTIFICATION_STATUSES,
  NOTIFICATION_SUPPRESSIONS,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { sortedListRequest } from './common/list.js';
import { paginated } from './common/pagination.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

const isoDaySchema = z.iso.date('a day is a real YYYY-MM-DD date');

/**
 * One delivery, as the operator's screen reads it. **`destination` is masked by
 * the server** — `yu***@g***.com`, `+90***34` — because the list is one screen
 * carrying every donor's and every colleague's address, and reading a delivery
 * record is not a reason to be handed the addresses in it. What was actually
 * sent to lives on the row and leaves the server nowhere.
 */
export const notificationSchema = z.object({
  id: z.uuid(),
  event: z.string(),
  channel: z.enum(NOTIFICATION_CHANNELS),
  audience: z.enum(NOTIFICATION_AUDIENCES),
  recipientName: z.string().nullable(),
  maskedDestination: z.string().nullable(),
  organizationName: z.string().nullable(),
  subject: z.string().nullable(),
  body: z.string(),
  status: z.enum(NOTIFICATION_STATUSES),
  suppressionReason: z.enum(NOTIFICATION_SUPPRESSIONS).nullable(),
  attempts: z.number().int(),
  failureCode: z.string().nullable(),
  causeEntity: z.string(),
  causeId: z.uuid().nullable(),
  templateVersion: z.number().int(),
  queuedAt: z.string(),
  settledAt: z.string().nullable(),
  readAt: z.string().nullable(),
});

export type Notification = z.infer<typeof notificationSchema>;

export const notificationPreferenceSchema = z.object({
  event: z.enum(NOTIFICATION_EVENT_NAMES).nullable(),
  channel: z.enum(NOTIFICATION_CHANNELS),
  frequency: z.enum(NOTIFICATION_FREQUENCIES),
});

export type NotificationPreference = z.infer<typeof notificationPreferenceSchema>;

/** What a person may choose between: every event, its channels, and its defaults. */
export const notificationPreferencesSchema = z.object({
  events: z.array(
    z.object({
      event: z.enum(NOTIFICATION_EVENT_NAMES),
      audience: z.enum(NOTIFICATION_AUDIENCES),
      channels: z.array(z.enum(NOTIFICATION_CHANNELS)),
      defaults: z.array(z.enum(NOTIFICATION_CHANNELS)),
    }),
  ),
  chosen: z.array(notificationPreferenceSchema),
});

/** One event's Turkish, rendered from example values, as the admin previews it. */
export const notificationTemplateSchema = z.object({
  event: z.enum(NOTIFICATION_EVENT_NAMES),
  channel: z.enum(NOTIFICATION_CHANNELS),
  templateVersion: z.number().int(),
  whatsappTemplate: z.string(),
  subject: z.string().nullable(),
  body: z.string(),
  variables: z.array(z.string()),
});

export const NOTIFICATION_SORT_FIELDS = ['queuedAt', 'event'] as const;

/** How many of a person's own in-app notifications the bell holds at once. */
export const NOTIFICATION_CENTRE_LIMIT = 30;

export const notificationContract = {
  list: oc
    .route({
      method: 'GET',
      path: '/notifications',
      summary: 'Every notification delivery, newest first; global scope only',
    })
    .errors(guarded)
    .input(
      sortedListRequest(NOTIFICATION_SORT_FIELDS, 'queuedAt', 'desc')
        .omit({ search: true })
        .extend({
          status: z.enum(NOTIFICATION_STATUSES).optional(),
          channel: z.enum(NOTIFICATION_CHANNELS).optional(),
          event: z.enum(NOTIFICATION_EVENT_NAMES).optional(),
          queuedFrom: isoDaySchema.optional(),
          queuedTo: isoDaySchema.optional(),
        }),
    )
    .output(paginated(notificationSchema)),

  get: oc
    .route({
      method: 'GET',
      path: '/notifications/{id}',
      summary: 'One delivery and what a provider answered; global scope only',
    })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(notificationSchema),

  templates: oc
    .route({
      method: 'GET',
      path: '/notifications/templates',
      summary: 'Every Turkish template, rendered from example values',
    })
    .errors(guarded)
    .input(z.object({ channel: z.enum(NOTIFICATION_CHANNELS).optional() }))
    .output(z.object({ items: z.array(notificationTemplateSchema) })),

  mine: oc
    .route({
      method: 'GET',
      path: '/notifications/mine',
      summary: 'The signed-in user’s own in-app notifications and unread count',
    })
    .errors(guarded)
    .input(z.object({}))
    .output(
      z.object({ items: z.array(notificationSchema), unread: z.number().int().nonnegative() }),
    ),

  markRead: oc
    .route({
      method: 'POST',
      path: '/notifications/{id}/read',
      summary: 'Mark one of the caller’s own in-app notifications read',
    })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ unread: z.number().int().nonnegative() })),

  markAllRead: oc
    .route({
      method: 'POST',
      path: '/notifications/read-all',
      summary: 'Mark every one of the caller’s own in-app notifications read',
    })
    .errors(guarded)
    .input(z.object({}))
    .output(z.object({ read: z.number().int().nonnegative() })),

  preferences: oc
    .route({
      method: 'GET',
      path: '/notifications/preferences',
      summary: 'What the caller has chosen to be told about, and on which channel',
    })
    .errors(guarded)
    .input(z.object({}))
    .output(notificationPreferencesSchema),

  setPreference: oc
    .route({
      method: 'PUT',
      path: '/notifications/preferences',
      summary: 'Set the caller’s own channel and frequency for an event, or for all of them',
    })
    .errors(guarded)
    .input(notificationPreferenceSchema)
    .output(notificationPreferencesSchema),
};
